// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

export const PATIENT_EXTENSION_URLS = {
  birthSex: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
  genderIdentity: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity',
  pronouns: 'http://hl7.org/fhir/StructureDefinition/individual-pronouns',
  coveragePhone: 'http://polarissleep.com/fhir/StructureDefinition/coverage-phone',
  treatment: 'https://polarissleep.com/fhir/StructureDefinition/patient-treatment',
  referringOrganization: 'https://polarissleep.com/fhir/StructureDefinition/patient-referring-organization',
  referringProvider: 'https://polarissleep.com/fhir/StructureDefinition/patient-referring-provider',
} as const;

export const COVERAGE_CLASS_CODES = {
  plan: 'plan',
  group: 'group',
} as const;

export const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';

export const LOINC_SYSTEM = 'http://loinc.org';

export const TITLE_OPTIONS = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
];

/**
 * Polaris-local code system used for v2 (SLE-243) gender-identity codes. No
 * widely-accepted SNOMED concept exists for Cisgender; ONC's LL3322-6 omits it.
 */
export const POLARIS_CODES_SYSTEM = 'http://polarissleep.com/codes';

/**
 * Patient profile-edit dropdown for sex at birth. UNION of v2 intake form options
 * (top) + legacy null-flavor options (bottom — pre-v2 patients still carry these),
 * so editing a legacy patient's profile resolves their current value in the
 * dropdown without silently clearing the field on save.
 */
export const SEX_AT_BIRTH_OPTIONS = [
  // v2 (SLE-243) intake form set:
  { value: 'M', label: 'Male', system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender' },
  { value: 'F', label: 'Female', system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender' },
  { value: '32570681000036106', label: 'Intersex', system: 'http://snomed.info/sct' },
  // Legacy options for pre-v2 patients.
  { value: 'ASKU', label: 'Asked but unknown', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor' },
  { value: 'OTH', label: 'Other', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor' },
  { value: 'UNK', label: 'Unknown', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor' },
  {
    value: 'asked-declined',
    label: 'Asked but declined',
    system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
  },
];

export type SexAtBirthCode = (typeof SEX_AT_BIRTH_OPTIONS)[number]['value'];

/**
 * Patient profile-edit dropdown for gender identity. UNION of v2 + legacy.
 * For v2 entries `fhirGender` is set to the code itself so setGenderIdentity
 * round-trips the v2 code onto Patient.gender without coercing or clearing.
 */
export const GENDER_IDENTITY_OPTIONS = [
  // v2 (SLE-243) intake form set:
  {
    value: 'cisgender',
    label: 'Cisgender',
    display: 'Cisgender',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'cisgender' as const,
  },
  {
    value: 'transgender',
    label: 'Transgender',
    display: 'Transgender',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'transgender' as const,
  },
  {
    value: 'nonbinary',
    label: 'Nonbinary',
    display: 'Nonbinary',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'nonbinary' as const,
  },
  {
    value: 'prefer-not-to-say',
    label: 'Prefer not to say',
    display: 'Prefer not to say',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'prefer-not-to-say' as const,
  },
  {
    value: 'other',
    label: 'Other',
    display: 'Other',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'other' as const,
  },
  // Legacy options for pre-v2 patients.
  {
    value: '446141000124107',
    label: 'Identifies as female gender',
    display: 'Identifies as female gender (finding)',
    system: 'http://snomed.info/sct',
    fhirGender: 'female' as const,
  },
  {
    value: '446151000124109',
    label: 'Identifies as male gender',
    display: 'Identifies as male gender (finding)',
    system: 'http://snomed.info/sct',
    fhirGender: 'male' as const,
  },
  {
    value: '33791000087105',
    label: 'Identifies as nonbinary gender',
    display: 'Identifies as nonbinary gender',
    system: 'http://snomed.info/sct',
    fhirGender: 'other' as const,
  },
  {
    value: 'UNK',
    label: 'Unknown',
    display: 'Unknown',
    system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
    fhirGender: 'unknown' as const,
  },
  {
    value: 'asked-declined',
    label: 'Asked but declined',
    display: 'Asked But Declined',
    system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
    fhirGender: 'unknown' as const,
  },
];

export const PRONOUN_OPTIONS = [
  { value: 'LA29518-0', label: 'He/Him' },
  { value: 'LA29519-8', label: 'She/Her' },
  { value: 'LA29520-6', label: 'They/Them' },
];

export const US_STATE_OPTIONS = [
  { value: 'AL', label: 'AL' },
  { value: 'AK', label: 'AK' },
  { value: 'AZ', label: 'AZ' },
  { value: 'AR', label: 'AR' },
  { value: 'CA', label: 'CA' },
  { value: 'CO', label: 'CO' },
  { value: 'CT', label: 'CT' },
  { value: 'DE', label: 'DE' },
  { value: 'DC', label: 'DC' },
  { value: 'FL', label: 'FL' },
  { value: 'GA', label: 'GA' },
  { value: 'HI', label: 'HI' },
  { value: 'ID', label: 'ID' },
  { value: 'IL', label: 'IL' },
  { value: 'IN', label: 'IN' },
  { value: 'IA', label: 'IA' },
  { value: 'KS', label: 'KS' },
  { value: 'KY', label: 'KY' },
  { value: 'LA', label: 'LA' },
  { value: 'ME', label: 'ME' },
  { value: 'MD', label: 'MD' },
  { value: 'MA', label: 'MA' },
  { value: 'MI', label: 'MI' },
  { value: 'MN', label: 'MN' },
  { value: 'MS', label: 'MS' },
  { value: 'MO', label: 'MO' },
  { value: 'MT', label: 'MT' },
  { value: 'NE', label: 'NE' },
  { value: 'NV', label: 'NV' },
  { value: 'NH', label: 'NH' },
  { value: 'NJ', label: 'NJ' },
  { value: 'NM', label: 'NM' },
  { value: 'NY', label: 'NY' },
  { value: 'NC', label: 'NC' },
  { value: 'ND', label: 'ND' },
  { value: 'OH', label: 'OH' },
  { value: 'OK', label: 'OK' },
  { value: 'OR', label: 'OR' },
  { value: 'PA', label: 'PA' },
  { value: 'RI', label: 'RI' },
  { value: 'SC', label: 'SC' },
  { value: 'SD', label: 'SD' },
  { value: 'TN', label: 'TN' },
  { value: 'TX', label: 'TX' },
  { value: 'UT', label: 'UT' },
  { value: 'VT', label: 'VT' },
  { value: 'VA', label: 'VA' },
  { value: 'WA', label: 'WA' },
  { value: 'WV', label: 'WV' },
  { value: 'WI', label: 'WI' },
  { value: 'WY', label: 'WY' },
];

export const HEIGHT_LOINC_CODE = '8302-2';
export const WEIGHT_LOINC_CODE = '29463-7';
export const VITAL_SIGNS_CATEGORY_SYSTEM = 'http://terminology.hl7.org/CodeSystem/observation-category';
export const UCUM_SYSTEM = 'http://unitsofmeasure.org';
